import React, { type FunctionComponent } from 'react'
import { type FormContextValues } from 'react-hook-form'
import { css } from '@emotion/react'
import { Text, Button, colors, Modal, useBooleanState } from '@retailer-platform/shared-components'
import {
  type HeroBannerBannerType,
  type I18nHeroBannerFormData,
} from 'domains/merchandising/types/heroBanner.types'
import { MerchandisingI18nFormFieldContainer } from 'domains/merchandising/components/i18n-form-field-container/MerchandisingI18nFormFieldContainer'
import { MerchandisingFormField } from 'domains/merchandising/components/form-field/MerchandisingFormField'
import { MerchandisingFormRow } from 'domains/merchandising/components/form-row/MerchandisingFormRow'
import { MerchandisingFormInput } from 'domains/merchandising/components/form-input/MerchandisingFormInput'
import { ContentManagementConfigurableButton } from 'domains/merchandising/api'
import { useDashMessages } from 'intl/intl.hooks'
import { isTextBanner } from 'domains/merchandising/utils/heroBannerUtils'
import { MerchandisingFormSectionDivider } from 'domains/merchandising/components/form-section/MerchandisingFormSectionDivider'
import { removeLocaleKeys } from 'domains/merchandising/utils/i18n'
import { useLocales } from 'domains/merchandising/utils/useLocales.hooks'
import { HeroBannerDetailsPreview } from 'domains/merchandising/components/hero-banner-details/components/HeroBannerDetailsPreview'
import {
  HeroBannerFormContentImageInput,
  type Props as ImageInputProps,
} from './HeroBannerFormContentImageInput'

interface Props {
  type: HeroBannerBannerType
  form: FormContextValues<I18nHeroBannerFormData>
}

type bannerOptions = {
  [key in HeroBannerBannerType]: ImageInputProps[]
}

const altTextDescriptionStyles = css({
  color: colors.GRAYSCALE.X70,
  margin: 0,
})

const actionRowStyles = css({
  display: 'flex',
  justifyContent: 'flex-end',
})

const bannersForType: bannerOptions = {
  TEXT: [
    {
      identifier: 'images.large',
      labelId: 'merchandising.hero-banners.section.content.text.image',
      previewDimensions: { width: 314, height: 314 },
      imageDimensions: { width: 2000, height: 2000 },
    },
  ],
  IMAGE: [
    {
      identifier: 'images.large',
      labelId: 'merchandising.hero-banners.section.content.image.large',
      previewDimensions: { width: 546, height: 153 },
      imageDimensions: { width: 4368, height: 1222 },
    },
    {
      identifier: 'images.small',
      labelId: 'merchandising.hero-banners.section.content.image.small',
      previewDimensions: { width: 314, height: 175 },
      imageDimensions: { width: 2732, height: 1036 },
    },
  ],
}

export const HeroBannerFormContentImage: FunctionComponent<Props> = ({ type, form }) => {
  const banners = bannersForType[type]
  const messages = useDashMessages({
    preview: 'merchandising.hero-banners.section.content.image.preview',
    altText: 'merchandising.hero-banners.section.content.image.altTextDescription',
  })

  const { errors, register, getValues, formState } = form

  const [previewOpen, showPreview, closePreview] = useBooleanState(false)

  const [locale] = useLocales()

  return (
    <div>
      {isTextBanner(type) && <MerchandisingFormSectionDivider />}

      {banners?.map(banner => (
        <React.Fragment key={banner.identifier}>
          <MerchandisingI18nFormFieldContainer
            form={form}
            labelPrefixed
            field={locale => (
              <HeroBannerFormContentImageInput
                {...banner}
                identifier={`${banner.identifier}.${locale}`}
                defaultUrl={getValues(`${banner.identifier}.${locale}`)}
                ref={register({ required: true })}
                errors={errors}
              />
            )}
          />
          <MerchandisingFormSectionDivider />
        </React.Fragment>
      ))}

      <MerchandisingFormRow>
        <MerchandisingFormField fullWidth>
          <MerchandisingI18nFormFieldContainer
            form={form}
            labelPrefixed
            field={locale => (
              <MerchandisingFormInput
                identifier={`imageAltText.${locale}`}
                defaultValue={getValues(`imageAltText.${locale}`)}
                labelId="merchandising.hero-banners.section.content.image.altTextLabel"
                ref={register({ required: true })}
                errors={errors}
              />
            )}
          />
          <Text styles={altTextDescriptionStyles} size="small">
            {messages.altText}
          </Text>
        </MerchandisingFormField>
      </MerchandisingFormRow>

      <MerchandisingFormSectionDivider />

      <div css={actionRowStyles}>
        <Button disabled={!formState.isValid} onClick={showPreview} variant="secondary">
          {messages.preview}
        </Button>
      </div>

      <Modal
        isOpen={previewOpen}
        onRequestClose={closePreview}
        maxWidth={1200}
        maxHeight={900}
        bodyContent={
          <HeroBannerDetailsPreview
            button={removeLocaleKeys(getValues('button'), locale)}
            images={removeLocaleKeys(getValues('images'), locale)}
            imageAltText={removeLocaleKeys(getValues('imageAltText'), locale)}
            descriptionText={removeLocaleKeys(getValues('descriptionText'), locale)}
            headingText={removeLocaleKeys(getValues('headingText'), locale)}
            subheadingText={removeLocaleKeys(getValues('subheadingText'), locale)}
            destination={getValues('destination')}
            type={getValues('type')}
            backgroundColor={removeLocaleKeys(getValues('backgroundColor'), locale)}
          />
        }
      />
    </div>
  )
}
